import React, { useState } from 'react';
import { useProperties } from '../hooks/useProperties';
import { useDeleteProperty } from '../hooks/useDeleteProperty';
import { useQueryClient } from '@tanstack/react-query';
import { getStatusColor } from '../utils/statusUtils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import useDebounce from '../hooks/useDebounce';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  Building2,
} from 'lucide-react';

const Properties = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [status, setStatus] = useState('');

  // Debounce search and property type filters
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedPropertyType = useDebounce(propertyType, 500);
  
  const filters = {};
  if (debouncedSearchQuery) filters.search = debouncedSearchQuery;
  if (debouncedPropertyType) filters.type = debouncedPropertyType;
  if (status) filters.status = status;
  
  const { data: propertiesData, isLoading } = useProperties(currentPage, filters);
  const { mutate: deleteProperty, isPending: isDeleting } = useDeleteProperty();
  const queryClient = useQueryClient();
  const properties = propertiesData?.properties || [];
  const pagination = propertiesData?.pagination;
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPropertyForDelete, setSelectedPropertyForDelete] = useState(null);

  const handleDeleteProperty = (property) => {
    setSelectedPropertyForDelete(property);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedPropertyForDelete) {
      deleteProperty(selectedPropertyForDelete.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['properties'] });
          queryClient.refetchQueries({ queryKey: ['properties', currentPage] });
          toast.success('Property deleted successfully');
          setCurrentPage(1);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to delete property');
        },
        onSettled: () => {
          setIsDeleteModalOpen(false);
          setSelectedPropertyForDelete(null);
        },
      });
    }
  };



  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand">Properties</h1>
          <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Manage your property portfolio</p>
        </div>
        <button
          onClick={() => navigate('/properties/add')}
          className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 text-sm sm:text-base"
        >
          <Plus size={18} />
          <span>Add Property</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-300 p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
          {/* Search Input */}
          <div>
            <label className="block text-xs font-medium text-prose-black mb-1 sm:mb-2">Search by Name/Address</label>
            <input
              type="text"
              placeholder="e.g., Lagos, Ibadan..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-xs font-medium text-prose-black mb-1 sm:mb-2">Property Type</label>
            <input
              type="text"
              placeholder="e.g., apartment, villa..."
              value={propertyType}
              onChange={(e) => {
                setPropertyType(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-prose-black mb-1 sm:mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand/50 text-xs"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(searchQuery || propertyType || status) && (
          <button
            onClick={() => {
              setSearchQuery('');
              setPropertyType('');
              setStatus('');
              setCurrentPage(1);
            }}
            className="mt-2 px-3 py-1.5 text-xs font-medium text-prose-brand border border-prose-brand hover:bg-prose-brand hover:text-white rounded-lg transition duration-200"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Properties Table */}
      {isLoading ? (
        <LoadingSpinner message="Loading properties..." />
      ) : (
      <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">
                  Name
                </th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Location</th>
                <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Type</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Price</th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Status</th>
                <th className="px-3 sm:px-6 py-2 sm:py-3 text-left font-semibold text-prose-brand">Actions</th>
              </tr>
            </thead>
            <tbody>
              {properties.length > 0 ? (
                properties.map((property) => (
                  <tr
                    key={property.id}
                    className="border-b border-gray-300/60 hover:bg-gray-50 transition duration-200"
                  >
                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-prose-black text-xs sm:text-sm">{property.title}</td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black text-xs sm:text-sm">{property.address}</td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 text-prose-black/80 text-xs">{property.type}</td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-semibold text-prose-gold-dark text-xs sm:text-sm">
                      £{property.price}
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(property.status)}`}>
                        <CheckCircle size={12} />
                        <span className="capitalize">{property.status}</span>
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => navigate(`/properties/${property.id}`)}
                          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition duration-200 text-prose-brand"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/properties/${property.id}/edit`)}
                          className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition duration-200 text-prose-brand"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteProperty(property)}
                          className="p-1.5 sm:p-2 hover:bg-red-100 rounded-lg transition duration-200 text-red-500"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-3 sm:px-6 py-12 sm:py-16">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="p-3 sm:p-4 rounded-full bg-gray-100">
                        <Building2 size={32} className="text-prose-black/40" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm sm:text-base font-semibold text-prose-black/70">No properties found</p>
                        <p className="text-xs sm:text-sm text-prose-black/50 mt-1">
                          {searchQuery || propertyType || status
                            ? 'Try adjusting your filters'
                            : 'Start by adding your first property'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer with Pagination */}
        {properties.length > 0 && (
          <div className="bg-white border-t border-gray-300 px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-end gap-3 sm:gap-4 flex-col sm:flex-row">
              <Pagination
                pagination={pagination}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        )}
      </div>
      )}

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPropertyForDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        propertyTitle={selectedPropertyForDelete?.title || 'Property'}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Properties;
